import React, { useEffect } from 'react';
import './Image.scss';


/**
 * Renders an overlay image.
 *
 * The img is itself the positioned element: percentage max-width / max-height on it
 * resolve against the main image. An unsized overlay scales down on both axes when
 * either cap binds; an authored width keeps its box and the stylesheet pins the art to
 * the overlay's corner inside it.
 *
 * Overlay assets are exported at 2x. The asset is offered only as a `2x` srcset
 * candidate, with no `src`, so every display divides its intrinsic size by two and an
 * unsized overlay draws at its designed size without a script. A `src` would be treated
 * as an implicit `1x` candidate and picked on standard-density screens, doubling it.
 */
const renderOverlay = (
  src: string | undefined,
  alt: string | undefined,
  position: OverlayPosition,
  width: string | undefined,
  overlayNumber: 1 | 2
) => {
  if (!src) return null;

  // srcset tokenizes on whitespace: a space inside the URL ends it early and the candidate
  // is dropped, where a plain src would have percent-encoded it. Commas are safe (only a
  // trailing comma acts as a separator).
  const srcsetUrl = src.replace(/\s/g, encodeURIComponent);

  return (
    <img
      srcSet={`${srcsetUrl} 2x`}
      alt={alt ?? ''}
      className={`image__overlay image__overlay--overlay${overlayNumber} image__overlay--${position}`}
      style={width ? { width } : undefined}
    />
  );
};

/**
 * Image - Semantic HTML image wrapper for Image blocks
 * 
 * Extends standard HTML img with Poly-specific convenience features:
 * - `url` prop as CMS-friendly alias for src
 * - `caption` prop for automatic figure/figcaption structure
 * - `variant` prop for common image treatments (headshot, etc.)
 * - `overlay1`, `overlay2` props for layering images on top
 * - Automatic accessibility handling:
 *   - If caption exists: Uses caption as alt text (if alt not provided)
 *   - If alt exists: Uses provided alt text
 *   - If neither: Marks as decorative (alt="", role="presentation")
 * - All other HTML img attributes pass through unchanged
 * 
 * Uses semantic HTML since Cloudscape doesn't provide an image component.
 */

export type OverlayPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';

export const VALID_OVERLAY_POSITIONS: readonly OverlayPosition[] = [
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
  'center',
];

const isValidOverlayPosition = (value: unknown): value is OverlayPosition =>
  typeof value === 'string' && (VALID_OVERLAY_POSITIONS as readonly string[]).includes(value);

const resolveOverlayPosition = (
  value: OverlayPosition | undefined,
  fallback: OverlayPosition,
  overlayNumber: 1 | 2,
): OverlayPosition => {
  if (value === undefined || isValidOverlayPosition(value)) {
    return value ?? fallback;
  }
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console -- surface CMS mis-config to authors during dev
    console.warn(
      `[Image] overlay${overlayNumber}Position "${String(value)}" is not a valid position — using fallback "${fallback}". Valid: ${VALID_OVERLAY_POSITIONS.join(', ')}.`,
    );
  }
  return fallback;
};

/** Where the main image's focal point sits in its box (CSS `object-position`). */
export type ImageAlignment =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'left'
  | 'center'
  | 'right'
  | 'bottom-left'
  | 'bottom'
  | 'bottom-right';

export const VALID_IMAGE_ALIGNMENTS: readonly ImageAlignment[] = [
  'top-left',
  'top',
  'top-right',
  'left',
  'center',
  'right',
  'bottom-left',
  'bottom',
  'bottom-right',
];

const isValidImageAlignment = (value: unknown): value is ImageAlignment =>
  typeof value === 'string' && (VALID_IMAGE_ALIGNMENTS as readonly string[]).includes(value);

/**
 * @polySemanticUsage Placeholder for now
 */
export interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  // Poly-specific props
  url: string;
  caption?: string;
  variant?: 'default' | 'headshot';
  /** How image fills its container: 'scale-down' (default), 'cover' (crop to fill), 'contain' (fit within) */
  objectFit?: 'scale-down' | 'cover' | 'contain';
  /**
   * Where the image sits in its box: with 'cover' it picks which part stays visible when
   * cropped, with 'contain' it picks where the image sits in the leftover space. Invalid
   * values fall back to 'center'.
   */
  alignment?: ImageAlignment;
  /**
   * Render as a bare <img> without the <figure> wrapper.
   * Use for inline decorative icons inside interactive elements (<a> or <button>)
   * where a <figure> would introduce incorrect semantic structure.
   */
  bare?: boolean;
  
  // Overlay 1 props
  /** Overlay 1 image URL (positioned on top of main image) */
  overlay1Url?: string;
  /** Overlay 1 image alt text */
  overlay1Alt?: string;
  /** Overlay 1 image position */
  overlay1Position?: OverlayPosition;
  /**
   * Overlay 1 width in pixels, e.g. '240px'. Leave unset to render the asset at its
   * designed size (assets are exported at 2x). Capped to the image minus the offsets.
   */
  overlay1Width?: string;

  // Overlay 2 props
  /** Overlay 2 image URL (positioned on top of main image) */
  overlay2Url?: string;
  /** Overlay 2 image alt text */
  overlay2Alt?: string;
  /** Overlay 2 image position */
  overlay2Position?: OverlayPosition;
  /**
   * Overlay 2 width in pixels, e.g. '240px'. Leave unset to render the asset at its
   * designed size (assets are exported at 2x). Capped to the image minus the offsets.
   */
  overlay2Width?: string;

  // width and height inherited from React.ImgHTMLAttributes (string | number | undefined)
}

export const Image: React.FC<ImageProps> = ({
  url,
  caption,
  variant = 'default',
  objectFit = 'scale-down',
  alignment,
  bare = false,
  width,
  height,
  style,
  alt,
  role,
  className,
  overlay1Url,
  overlay1Alt,
  overlay1Position = 'bottom-right',
  overlay1Width,
  overlay2Url,
  overlay2Alt,
  overlay2Position = 'top-left',
  overlay2Width,
  ...imgProps
}) => {
  // Accessibility logic: Determine if image is decorative or meaningful
  // Priority: caption > alt > role="presentation"
  const isDecorative = !caption && !alt;
  const accessibilityProps = isDecorative
    ? { alt: '', role: role || 'presentation' }
    : { alt: alt || caption || '' };

  const resolvedAlignment = isValidImageAlignment(alignment) ? alignment : 'center';

  useEffect(() => {
    // A blank CMS value means "unset", so only an unknown non-empty value is worth flagging.
    if (process.env.NODE_ENV !== 'production' && alignment && !isValidImageAlignment(alignment)) {
      // eslint-disable-next-line no-console -- surface CMS mis-config to authors during dev
      console.warn(
        `[Image] alignment "${String(alignment)}" is not a valid alignment — using "center". Valid: ${VALID_IMAGE_ALIGNMENTS.join(', ')}.`,
      );
    }
  }, [alignment]);

  const baseFigureClassName = `image ${variant !== 'default' ? `image--${variant}` : ''} ${objectFit ? `image--${objectFit}` : ''}`.trim();
  // Center is the stylesheet default, so it adds no class and existing markup is unchanged.
  const figureClassName = resolvedAlignment === 'center'
    ? baseFigureClassName
    : `${baseFigureClassName} image--align-${resolvedAlignment}`;
  
  // For custom dimensions, apply via style prop
  const dimensionStyle = (width || height) ? {
    ...(width && { width }),
    ...(height && { height }),
  } : undefined;
  const imgStyle = (dimensionStyle || style) ? { ...dimensionStyle, ...style } : undefined;
  
  // Check if any overlays are present
  const hasOverlays = overlay1Url || overlay2Url;

  // Validate position values against the OverlayPosition allow-list. React's
  // default-parameter values only fire when the prop is `undefined`, so an
  // empty string or unknown value from the CMS bypasses the default and would
  // render an unresolved `image__overlay--` class (visually silent). Fall back
  // to the variant default when the value is invalid.
  const resolvedOverlay1Position = resolveOverlayPosition(overlay1Position, 'bottom-right', 1);
  const resolvedOverlay2Position = resolveOverlayPosition(overlay2Position, 'top-left', 2);

  // Deduplicate overlay positions — skip later overlays that share a position
  // with an earlier one to prevent visual stacking.
  const usedPositions = new Set<OverlayPosition>();
  const shouldRenderOverlay = (url: string | undefined, position: OverlayPosition, num: number): boolean => {
    if (!url) return false;
    if (usedPositions.has(position)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[Image] overlay${num} skipped: position "${position}" already used by a previous overlay.`);
      }
      return false;
    }
    usedPositions.add(position);
    return true;
  };

  const render1 = shouldRenderOverlay(overlay1Url, resolvedOverlay1Position, 1);
  const render2 = shouldRenderOverlay(overlay2Url, resolvedOverlay2Position, 2);
  
  // Main image element
  const mainImage = (
    <img
      {...imgProps}
      {...accessibilityProps}
      src={url}
      // bare: skip image__img (avoids block-display/width:100% on inline icons); non-bare always gets image__img
      className={bare ? (className || undefined) : `image__img ${className || ''}`.trim()}
      style={imgStyle}
    />
  );

  if (bare) return mainImage;

  return (
    <figure className={figureClassName}>
      {hasOverlays ? (
        // The wrapper is the overlays' positioning parent, so it must match the image box.
        // An authored width or height would otherwise leave the wrapper at the container's
        // size and anchor overlays outside the image.
        <div className="image__wrapper" style={dimensionStyle}>
          {mainImage}
          {render1 && renderOverlay(overlay1Url, overlay1Alt, resolvedOverlay1Position, overlay1Width, 1)}
          {render2 && renderOverlay(overlay2Url, overlay2Alt, resolvedOverlay2Position, overlay2Width, 2)}
        </div>
      ) : (
        mainImage
      )}
      {caption && (
        <figcaption className="image__caption">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};
